import React from 'react'
import { useForm, usePage, Link } from '@inertiajs/react'
import { Mail, RefreshCw, AlertCircle, CheckCircle2 } from 'lucide-react'
import AuthLayout from '@/layouts/AuthLayout'
import { Button } from '@/components/ui/button'
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from '@/components/ui/card'
import { Alert, AlertDescription } from '@/components/ui/alert'

type Props = {
  email: string
  errors?: Record<string, string[]>
}

export default function ConfirmationPending({ email, errors = {} }: Props) {
  const { flash } = usePage().props
  const { post, processing } = useForm({ email })

  const handleResend = () => {
    post('/confirmation')
  }

  return (
    <AuthLayout>
      <Card className="w-full max-w-md">
        <CardHeader className="text-center">
          <div className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-primary/10">
            <Mail className="h-8 w-8 text-primary" />
          </div>
          <CardTitle className="text-xl">メールを確認してください</CardTitle>
          <CardDescription>確認メールを送信しました</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {flash?.notice && (
            <Alert>
              <CheckCircle2 className="h-4 w-4" />
              <AlertDescription>{flash.notice}</AlertDescription>
            </Alert>
          )}

          {errors.email && (
            <Alert variant="destructive">
              <AlertCircle className="h-4 w-4" />
              <AlertDescription>{errors.email[0]}</AlertDescription>
            </Alert>
          )}

          <p className="text-center text-sm text-muted-foreground">
            以下のメールアドレスに確認メールを送信しました。
            メール内のリンクをクリックしてアカウントを有効化してください。
          </p>

          <p className="rounded-lg bg-muted px-4 py-3 text-center font-medium">
            {email}
          </p>

          <div className="space-y-2 text-center text-sm text-muted-foreground">
            <p>メールが届かない場合は、迷惑メールフォルダをご確認ください。</p>
            <p>
              確認リンクの有効期限は <span className="font-medium">3日間</span>{' '}
              です。
            </p>
          </div>
        </CardContent>
        <CardFooter className="flex flex-col gap-4">
          <Button
            onClick={handleResend}
            disabled={processing}
            variant="outline"
            className="w-full"
          >
            <RefreshCw
              className={`mr-2 h-4 w-4 ${processing ? 'animate-spin' : ''}`}
            />
            確認メールを再送信
          </Button>
          <p className="text-sm text-muted-foreground">
            すでに確認済みの方は{' '}
            <Link href="/login" className="text-primary hover:underline">
              ログイン
            </Link>
          </p>
        </CardFooter>
      </Card>
    </AuthLayout>
  )
}
